import { useState } from "react";
import { ChevronDown, GraduationCap, Gauge } from "lucide-react";
import { useMode } from "@/context/ModeContext";
import { Hint } from "@/components/Hint";

// Beginner mode folds advanced panels behind a button with a plain-English note; Advanced mode renders them directly.
export const AdvancedSection = ({ title, what, why, k, children, testid = "advanced-section", defaultOpen = false }) => {
  const { beginner } = useMode();
  const [open, setOpen] = useState(defaultOpen);
  if (!beginner) return children;
  return (
    <div className="space-y-2" data-testid={testid}>
      <button data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
              className="w-full apx-card px-4 py-3 flex items-center gap-3 text-left border border-slate-800 hover:border-cyan-900/60 transition-colors">
        <div className="w-8 h-8 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center shrink-0"><Gauge className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium flex items-center gap-2">{open ? "Hide" : "Show"} advanced details · {title}{k && <Hint k={k} className="inline-flex" />}</div>
          {!open && <div className="text-xs text-slate-400 mt-0.5">{what}{why ? ` ${why}` : ""}</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <>
        <div className="text-xs text-slate-400 px-1" data-testid={`${testid}-explainer`}><strong className="text-slate-300">What this is:</strong> {what} {why && <><strong className="text-slate-300">Why it matters:</strong> {why}</>}</div>
        {children}
      </>}
    </div>
  );
};

// Toggle pill for the header / settings.
export const ModeSwitch = ({ compact = false }) => {
  const { mode, setMode } = useMode();
  return (
    <Hint title="Beginner / Advanced mode" text="Beginner adds plain-English explanations, examples and folds advanced tools behind 'Show advanced details'. Advanced shows every indicator and analytic at once. Nothing is removed in either mode — switch any time." asChild>
      <div role="radiogroup" aria-label="Display mode" data-testid="mode-switch" className="inline-flex rounded-full border border-slate-700 bg-slate-900/70 p-0.5 text-[11px] font-medium">
        {[["beginner", "Beginner", GraduationCap], ["advanced", "Advanced", Gauge]].map(([v, l, Icon]) => (
          <button key={v} role="radio" aria-checked={mode === v} data-testid={`mode-${v}`} onClick={() => setMode(v)}
                  className={`rounded-full px-2.5 py-1 inline-flex items-center gap-1 transition-colors ${mode === v ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400 hover:text-slate-200"}`}>
            <Icon className="w-3 h-3" />{!compact && l}
          </button>
        ))}
      </div>
    </Hint>
  );
};

// One-line beginner note under a chart / card. Hidden in Advanced mode.
export const BeginnerNote = ({ children, testid = "beginner-note" }) => {
  const { beginner } = useMode();
  if (!beginner) return null;
  return <div className="text-[11px] leading-snug text-slate-400 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2 mt-2" data-testid={testid}>{children}</div>;
};
